// src/App.js
import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { DisplayPreferencesProvider, useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { NavigationBarPreferencesProvider } from '../contexts/NavigationBarPreferencesContext';
import { StoreProvider } from '../contexts/StoreContext';
import { HomePreferencesProvider } from '../contexts/HomePreferencesContext';
import { BannersProvider } from '../contexts/BannersContext';
import { SocialMediaProvider } from '../contexts/SocialMediaContext';
import { AuthProvider } from '../contexts/AuthContext';
import { ProductsProvider } from '../contexts/ProductsContext';
import { BookmarksProvider } from '../contexts/BookmarksContext';
import { CartProvider } from '../contexts/CartContext';

import NavigationBar from './NavigationBar';
import CatalogView from './CatalogView';
import ProductDetail from './ProductDetail';
import StoreDetail from './StoreDetail';
import ContactView from './ContactView';
import GalleryPage from './GalleryPage';
import Footer from './Footer';
import CategoryDetail from './CategoryDetail';
import ProfileView from './ProfileView';
import CartView from './CartView';
import BookmarksView from './BookmarksView';
import OrdersView from './OrdersView';
import CheckoutView from './CheckoutView';
import OrderConfirmationPage from './OrderConfirmationPage';
import RequestView from './RequestView';
import SearchView from './SearchView';
import FAQView from './FAQView';
import TermsOfServiceView from './TermsOfServiceView';
import PrivacyPolicyView from './PrivacyPolicyView';
import MaintenanceView from './MaintenanceView';
import LoginView from './LoginView';
import ActiveCheckoutRecovery from '../components/ActiveCheckoutRecovery';

function ConditionalContactRoute() {
  const { showContact } = useDisplayPreferences();
  if (!showContact) return <Navigate to="/" replace />;
  return <ContactView />;
}

function ConditionalFAQRoute() {
  const { showFAQ } = useDisplayPreferences();
  if (!showFAQ) return <Navigate to="/" replace />;
  return <FAQView />;
}

function ConditionalCartRoute() {
  const { showCart, cartVisibilityLoading } = useDisplayPreferences();
  // Wait for both authoritative listeners before deciding whether to redirect.
  // This prevents a valid future payment-enabled store from being bounced
  // home merely because its live snapshot had not arrived yet.
  if (cartVisibilityLoading) return null;
  if (!showCart) return <Navigate to="/" replace />;
  return <CartView />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const { isUnderMaintenance, maintenanceMessage } = useDisplayPreferences();

  if (isUnderMaintenance) {
    return <MaintenanceView message={maintenanceMessage} />;
  }

  return (
    <div
      style={{
        padding: '0px',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <ScrollToTop />
      <NavigationBar />
      <ActiveCheckoutRecovery />
      <div style={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<CatalogView />} />
          <Route path="product/:slug" element={<ProductDetail />} />
          <Route path="category/:id" element={<CategoryDetail />} />
          <Route path="about" element={<StoreDetail />} />
          <Route path="contact" element={<ConditionalContactRoute />} />
          <Route path="request" element={<RequestView />} />
          <Route path="browse" element={<GalleryPage />} />
          <Route path="cart" element={<ConditionalCartRoute />} />
          <Route path="checkout" element={<CheckoutView />} />
          <Route path="order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="bookmarks" element={<BookmarksView />} />
          <Route path="orderhistory" element={<OrdersView />} />
          <Route path="orderhistory/:orderId" element={<OrderConfirmationPage />} />
          <Route path="login" element={<LoginView />} />
          <Route path="profile" element={<ProfileView />} />
          <Route path="search" element={<SearchView />} />
          <Route path="faq" element={<ConditionalFAQRoute />} />
          <Route path="terms" element={<TermsOfServiceView />} />
          <Route path="privacy" element={<PrivacyPolicyView />} />
        </Routes>
      </div>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <StoreProvider>
        <ProductsProvider>
          <BookmarksProvider>
            <CartProvider>
              <DisplayPreferencesProvider>
              <NavigationBarPreferencesProvider>
              <HomePreferencesProvider>
              <BannersProvider>
              <SocialMediaProvider>
              <AppContent />
              </SocialMediaProvider>
              </BannersProvider>
              </HomePreferencesProvider>
              </NavigationBarPreferencesProvider>
              </DisplayPreferencesProvider>
            </CartProvider>
          </BookmarksProvider>
        </ProductsProvider>
      </StoreProvider>
    </AuthProvider>
  );
}
